import {
  groupElementsBySection,
  kindSupportsOwnerLink,
  resolveElementOwner,
} from '@/etc/aiElements'
import { MOODBOARD_FIELDS, normalizeMoodboard } from '@/etc/projectMoodboard'
import { normalizeProjectGoals } from '@/etc/projectGoals'
import { composeCameraProfilePhrase } from '@/etc/cameraProfile'

// Builds the dynamic project-context knowledge document handed to the Creative
// Ask agent. Pure on purpose: `elements` arrives as an argument rather than
// being read from the store, so changing which elements are in scope (a future
// project-group concept, for example) is a change to the caller.

export const MAX_PROJECT_CONTEXT_CHARS = 120000
export const MAX_GOALS_CONTEXT_CHARS = 30000

const NOT_SPECIFIED = 'Not yet specified.'

// Personality and mood are deliberately absent: they describe how the assistant
// should behave, not what the project contains.
// `empty` is spelled out per kind because "Wardrobe" is a mass noun and does not
// pluralize like the others.
const DOCUMENT_KINDS = [
  {
    kind: 'character',
    heading: 'Characters',
    empty: 'No characters have been added to this project yet.',
  },
  {
    kind: 'wardrobe',
    heading: 'Wardrobe',
    empty: 'No wardrobe has been added to this project yet.',
  },
  {
    kind: 'prop',
    heading: 'Props',
    empty: 'No props have been added to this project yet.',
  },
  {
    kind: 'location',
    heading: 'Locations',
    empty: 'No locations have been added to this project yet.',
  },
]

const OWNED_KIND_LABELS = {
  wardrobe: 'Wardrobe',
  prop: 'Props',
}

function mention(name) {
  const raw = `${name || ''}`.trim()
  return raw ? `@${raw}` : ''
}

function cleanText(value) {
  return `${value || ''}`.replace(/\r\n/g, '\n').trim()
}

// Multi-line answers would otherwise break the one-fact-per-line shape the rest
// of the document relies on.
function inlineText(value) {
  return cleanText(value).replace(/\s*\n+\s*/g, ' / ')
}

function scopedElements(elements, projectId) {
  const wanted = `${projectId || ''}`.trim()
  if (!wanted) return []
  return (Array.isArray(elements) ? elements : []).filter(
    (element) => `${element?.projectId || ''}`.trim() === wanted,
  )
}

function renderOwnedBackReferences(character, scoped) {
  const lines = []
  Object.entries(OWNED_KIND_LABELS).forEach(([kind, label]) => {
    const owned = scoped
      .filter(
        (element) =>
          element.kind === kind &&
          resolveElementOwner(element, scoped)?.id === character.id,
      )
      .map((element) => mention(element.name))
      .filter(Boolean)
    if (owned.length) lines.push(`- ${label}: ${owned.join(', ')}`)
  })
  return lines
}

function renderElement(element, scoped) {
  const lines = [`#### ${mention(element.name) || '(unnamed)'}`]

  if (element.kind === 'character') {
    const height = inlineText(element.attributes?.height)
    const voiceTone = inlineText(element.attributes?.voiceTone)
    if (height) lines.push(`- Height: ${height}`)
    if (voiceTone) lines.push(`- Voice tone: ${voiceTone}`)
  }

  // Emitted on both sides of the link so "what does Sarah drive" is a lookup
  // under her heading rather than a scan of every prop description.
  if (kindSupportsOwnerLink(element.kind)) {
    const owner = resolveElementOwner(element, scoped)
    if (owner) lines.push(`- Belongs to: ${mention(owner.name)}`)
  }

  const description = cleanText(element.description)
  if (description) lines.push(`- Description: ${description}`)

  if (element.kind === 'character') {
    lines.push(...renderOwnedBackReferences(element, scoped))
  }

  return lines
}

function renderKindSection({ kind, heading, empty }, scoped) {
  const lines = [`## ${heading}`, '']
  const groups = groupElementsBySection(kind, scoped).filter(
    (group) => group.items.length,
  )

  if (!groups.length) {
    lines.push(empty, '')
    return lines
  }

  groups.forEach((group) => {
    lines.push(`### ${group.label}`, '')
    group.items.forEach((element) => {
      lines.push(...renderElement(element, scoped), '')
    })
  })

  return lines
}

function renderDesignSystem(moodboard, cameraProfile, cameraCatalog) {
  const lines = ['## Design System', '']
  const fields = MOODBOARD_FIELDS.filter((field) => field.key !== 'summary')
  const answered = fields.filter((field) => !!moodboard[field.key])
  const cameraPhrase = composeCameraProfilePhrase(
    cameraProfile,
    cameraCatalog,
  )

  if (!answered.length && !cameraPhrase) {
    lines.push(
      'No design system answers have been provided for this project yet.',
      '',
    )
    return lines
  }

  fields.forEach((field) => {
    const value = inlineText(moodboard[field.key])
    lines.push(`- ${field.label}: ${value || NOT_SPECIFIED}`)
  })
  lines.push(
    `- Default camera profile: ${inlineText(cameraPhrase) || NOT_SPECIFIED}`,
  )
  lines.push('')
  return lines
}

function renderGoals(goals) {
  const lines = ['## Goals', '']
  const normalized = normalizeProjectGoals(goals)
  if (!normalized.length) {
    lines.push('No goals have been added to this project yet.', '')
    return lines
  }

  let usedChars = 0
  let rendered = 0
  for (const [index, goal] of normalized.entries()) {
    const line = `- Goal ${index + 1}: ${inlineText(goal.description)}`
    if (usedChars + line.length > MAX_GOALS_CONTEXT_CHARS) break
    lines.push(line)
    usedChars += line.length
    rendered += 1
  }
  if (rendered < normalized.length) {
    lines.push(
      `- ${normalized.length - rendered} additional goal(s) omitted because the Goals section reached its context limit.`,
    )
  }
  lines.push('')
  return lines
}

export function buildProjectContextDoc({
  projectName = '',
  moodboard = null,
  cameraProfile = null,
  cameraCatalog = null,
  goals = [],
  elements = [],
  projectId = '',
  maxChars = MAX_PROJECT_CONTEXT_CHARS,
} = {}) {
  const header = [
    '# PROJECT CONTEXT',
    '',
    "Live state of the user's current media project, generated from its Moodboard",
    'answers and its Node Canvas Elements library. Treat this as the authoritative',
    'source for questions about this project. Where a field reads',
    `"${NOT_SPECIFIED}", the user has not provided it — say so rather than inventing one.`,
    '',
  ]

  const scopedProjectId = `${projectId || ''}`.trim()
  if (!scopedProjectId) {
    const text = [
      ...header,
      '## Status',
      '',
      'No project is currently open, so there is no project context available.',
      '',
    ].join('\n')
    return {
      text,
      charCount: text.length,
      truncated: false,
      elementCount: 0,
      goalCount: 0,
    }
  }

  const normalizedMoodboard = normalizeMoodboard(moodboard)
  const scoped = scopedElements(elements, scopedProjectId)
  const name = cleanText(projectName)
  const summary = cleanText(normalizedMoodboard.summary)

  const lines = [
    ...header,
    '## Project',
    '',
    `- Name: ${name || 'Untitled project'}`,
    '',
    '## Summary',
    '',
    summary || NOT_SPECIFIED,
    '',
    ...renderDesignSystem(
      normalizedMoodboard,
      cameraProfile,
      cameraCatalog,
    ),
    ...renderGoals(goals),
  ]

  DOCUMENT_KINDS.forEach((entry) => {
    lines.push(...renderKindSection(entry, scoped))
  })

  lines.push(
    '## Notes',
    '',
    '- Only elements saved to this project are listed. The user may also have',
    '  elements in their global library, which are not included here.',
    '- Personalities are system prompts that user can make on a node and they are intentionally excluded from this document.',
    '',
  )

  const full = lines.join('\n')
  const limit = Number.isFinite(maxChars) ? maxChars : MAX_PROJECT_CONTEXT_CHARS
  const truncated = full.length > limit
  const text = truncated
    ? `${full.slice(0, limit)}\n\n[Project context truncated because it exceeded the size limit.]`
    : full

  return {
    text,
    charCount: text.length,
    truncated,
    elementCount: scoped.filter((element) =>
      DOCUMENT_KINDS.some((entry) => entry.kind === element.kind),
    ).length,
    goalCount: normalizeProjectGoals(goals).length,
  }
}
